// Sayım girişi (web): barkod/koli okutma, ürün arama + elle miktar, satırlar, son girişler ve geri alma.
// Her okutma anında kaydedilir; aynı ürün tekrar okutulunca miktar üstüne eklenir.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImportCountExcelDialog from './ImportCountExcelDialog';
import { useTranslation } from 'react-i18next';
import { accountingApi } from '../../../../config/apiClient';
import { parseTrQuantity } from '../../../../utils/numberFormat';
import { ACCOUNTING_API } from '../../../../config/api';
import {
  fmtDate, fmtQty, stockCountService, type StockCountDetail, type StockCountEntry, type StockCountLine,
} from './stockCountService';

type Filter = 'all' | 'uncounted' | 'counted' | 'recount' | 'outOfScope';

interface Props {
  count: StockCountDetail;
  onChanged: () => void;
}

// Türkçe miktar: "1,5" ve "2.350" (ondalık) ile "1.500" (binlik) doğru okunur.
const parseQty = (v: string) => parseTrQuantity(v, 3);

const CountingPanel: React.FC<Props> = ({ count, onChanged }) => {
  const { i18n } = useTranslation();
  const tt = (tr: string, en: string) => ((i18n.language || '').startsWith('en') ? en : tr);

  const [lines, setLines] = useState<StockCountLine[]>(count.lines);
  const [entries, setEntries] = useState<StockCountEntry[]>([]);
  const [barcode, setBarcode] = useState('');
  const [qtyText, setQtyText] = useState('1');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'warning' | 'error' | 'info' } | null>(null);
  const [itemOptions, setItemOptions] = useState<any[]>([]);
  const [itemQuery, setItemQuery] = useState('');
  const [editLine, setEditLine] = useState<StockCountLine | null>(null);
  const [editText, setEditText] = useState('');
  const barcodeRef = useRef<HTMLInputElement | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => setLines(count.lines), [count.lines]);

  const loadEntries = useCallback(async () => {
    try {
      setEntries(await stockCountService.entries(count.id, { take: 30 }));
    } catch { /* liste boş kalır */ }
  }, [count.id]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const refreshLine = (lineId?: string | null, countedQty?: number | null) => {
    if (!lineId) return false;
    let found = false;
    setLines((prev) => prev.map((l) => {
      if (l.id !== lineId) return l;
      found = true;
      return { ...l, countedQty: countedQty ?? l.countedQty, lastCountedAt: new Date().toISOString(), recountRequested: false };
    }));
    return found;
  };

  const afterEntry = async (res: { lineId?: string | null; countedQty?: number | null; itemName?: string | null; isUnknownBarcode: boolean; isOutOfScope: boolean; packageName?: string | null; packageFactor?: number | null; quantity: number }) => {
    if (res.isUnknownBarcode) {
      setToast({ msg: tt('Bu barkod sistemde yok; listeye eklendi, incelemede eşleştirilir.', 'Unknown barcode; added to the list for review.'), severity: 'warning' });
    } else {
      const pkg = res.packageName ? ` (${res.packageName} +${fmtQty(res.packageFactor)})` : '';
      setToast({ msg: `${res.itemName}${pkg}: ${fmtQty(res.countedQty)}${res.isOutOfScope ? ` · ${tt('kapsam dışı', 'out of scope')}` : ''}`, severity: 'success' });
      if (!refreshLine(res.lineId, res.countedQty)) onChanged(); // yeni satır: listeyi yenile
    }
    loadEntries();
  };

  const submitBarcode = async () => {
    const code = barcode.trim();
    if (!code || busy) return;
    const qty = parseQty(qtyText || '1');
    if (!Number.isFinite(qty) || qty <= 0) {
      setToast({ msg: tt('Geçerli bir miktar girin.', 'Enter a valid quantity.'), severity: 'warning' });
      return;
    }
    setBusy(true);
    try {
      const res = await stockCountService.addEntry(count.id, {
        barcode: code, quantity: qty, entryQuantity: qty, method: qty === 1 ? 'SCAN' : 'MANUAL',
      });
      setBarcode('');
      setQtyText('1');
      await afterEntry(res);
    } catch (e: any) {
      setToast({ msg: e?.message || tt('Kaydedilemedi.', 'Could not save.'), severity: 'error' });
    } finally {
      setBusy(false);
      barcodeRef.current?.focus();
    }
  };

  // Ürün arama (barkodsuz ürünler için)
  useEffect(() => {
    const q = itemQuery.trim();
    if (q.length < 2) { setItemOptions([]); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const r = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, { pageNumber: 1, pageSize: 20, searchTerm: q, excludeVariantChildren: false });
        setItemOptions(r.data?.items ?? r.data?.Items ?? []);
      } catch { setItemOptions([]); }
    }, 250);
    return () => clearTimeout(timer);
  }, [itemQuery]);

  const addManual = async (item: any) => {
    if (!item) return;
    const qty = parseQty(qtyText || '1');
    if (!Number.isFinite(qty) || qty <= 0) {
      setToast({ msg: tt('Geçerli bir miktar girin.', 'Enter a valid quantity.'), severity: 'warning' });
      return;
    }
    setBusy(true);
    try {
      const res = await stockCountService.addEntry(count.id, {
        itemId: String(item.id ?? item.Id), quantity: qty, entryQuantity: qty, method: 'MANUAL',
      });
      setItemQuery('');
      setQtyText('1');
      await afterEntry(res);
    } catch (e: any) {
      setToast({ msg: e?.message || tt('Kaydedilemedi.', 'Could not save.'), severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const undo = async (entry: StockCountEntry) => {
    try {
      const res = await stockCountService.undo(count.id, entry.id);
      refreshLine(res.lineId, res.countedQty);
      setToast({ msg: tt('Giriş geri alındı.', 'Entry undone.'), severity: 'info' });
      loadEntries();
    } catch (e: any) {
      setToast({ msg: e?.message || tt('Geri alınamadı.', 'Could not undo.'), severity: 'error' });
    }
  };

  const saveTotal = async () => {
    if (!editLine) return;
    const qty = parseQty(editText);
    if (!Number.isFinite(qty) || qty < 0) {
      setToast({ msg: tt('Geçerli bir miktar girin.', 'Enter a valid quantity.'), severity: 'warning' });
      return;
    }
    try {
      const res = await stockCountService.setTotal(count.id, editLine.id, qty);
      refreshLine(res.lineId, res.countedQty);
      setEditLine(null);
      loadEntries();
    } catch (e: any) {
      setToast({ msg: e?.message || tt('Kaydedilemedi.', 'Could not save.'), severity: 'error' });
    }
  };

  const counts = useMemo(() => ({
    all: lines.length,
    uncounted: lines.filter((l) => l.countedQty == null).length,
    counted: lines.filter((l) => l.countedQty != null).length,
    recount: lines.filter((l) => l.recountRequested).length,
    outOfScope: lines.filter((l) => l.isOutOfScope).length,
  }), [lines]);

  const shown = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return lines.filter((l) => {
      if (filter === 'uncounted' && l.countedQty != null) return false;
      if (filter === 'counted' && l.countedQty == null) return false;
      if (filter === 'recount' && !l.recountRequested) return false;
      if (filter === 'outOfScope' && !l.isOutOfScope) return false;
      if (!q) return true;
      return [l.itemName, l.barcode, l.stockCode, l.lotNo].some((v) => (v || '').toLocaleLowerCase('tr-TR').includes(q));
    });
  }, [lines, filter, search]);

  const chip = (key: Filter, label: string) => (
    <Chip key={key} label={`${label} ${counts[key]}`} color={filter === key ? 'primary' : 'default'}
      variant={filter === key ? 'filled' : 'outlined'} onClick={() => setFilter(key)} size="small" />
  );

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, alignItems: 'start' }}>
      <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 1.5, display: 'grid', gap: 1.25 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 110px' }, gap: 1 }}>
            <TextField
              inputRef={barcodeRef}
              autoFocus
              size="small"
              placeholder={tt('Barkod okut (koli/paket barkodu da olur) ve Enter', 'Scan a barcode (case/pack too) and Enter')}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitBarcode(); } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {busy ? <CircularProgress size={16} /> : <QrCodeScannerIcon fontSize="small" />}
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              label={tt('Miktar', 'Qty')}
              value={qtyText}
              onChange={(e) => setQtyText(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              helperText={tt('Koli okutursanız koli adedi', 'Cases if a case is scanned')}
            />
          </Box>
          <Autocomplete
            size="small"
            options={itemOptions}
            filterOptions={(x) => x}
            inputValue={itemQuery}
            onInputChange={(_, v, reason) => { if (reason !== 'reset') setItemQuery(v); }}
            getOptionLabel={(o: any) => `${o.name ?? o.Name ?? ''}${(o.barcode ?? o.Barcode) ? ` · ${o.barcode ?? o.Barcode}` : ''}`}
            onChange={(_, v) => addManual(v)}
            value={null}
            renderInput={(p) => <TextField {...p} placeholder={tt('Barkod yoksa ürün ara ve seç (miktar yukarıdaki kutudan)', 'No barcode? Search a product (quantity from the box above)')} />}
          />
          {count.canEnterManually && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
                {tt("Excel'den al", 'Import from Excel')}
              </Button>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary">
            {tt('Her okutma anında kaydedilir. Aynı ürün tekrar okutulunca miktar üstüne eklenir; farklı rafta da olsa toplanır.',
              'Every scan is saved immediately. Scanning the same product again adds to it, even from another shelf.')}
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {chip('all', tt('Tümü', 'All'))}
          {chip('uncounted', tt('Sayılmadı', 'Uncounted'))}
          {chip('counted', tt('Sayıldı', 'Counted'))}
          {counts.recount > 0 && chip('recount', tt('Yeniden say', 'Recount'))}
          {counts.outOfScope > 0 && chip('outOfScope', tt('Kapsam dışı', 'Out of scope'))}
          <Box sx={{ flex: 1 }} />
          <TextField size="small" placeholder={tt('Listede ara', 'Search list')} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200 }} />
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 } }}>
                <TableCell>{tt('Ürün', 'Product')}</TableCell>
                <TableCell>{tt('Barkod', 'Barcode')}</TableCell>
                <TableCell>{tt('Lot / SKT', 'Lot / Exp.')}</TableCell>
                {count.showsExpected && <TableCell align="right">{tt('Başlangıçta', 'At start')}</TableCell>}
                <TableCell align="right">{tt('Sayılan', 'Counted')}</TableCell>
                <TableCell>{tt('Son giriş', 'Last entry')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((l) => (
                <TableRow key={l.id} sx={l.recountRequested ? { bgcolor: 'warning.light' } : undefined}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{l.itemName}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {l.isOutOfScope && <Chip size="small" color="warning" label={tt('Kapsam dışı', 'Out of scope')} />}
                      {l.recountRequested && <Chip size="small" color="warning" label={tt('Yeniden say', 'Recount')} />}
                      {l.hasManualEntry && <Chip size="small" variant="outlined" label={tt('Elle girildi', 'Typed')} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{l.barcode || '—'}</TableCell>
                  <TableCell>{l.lotNo ? `${l.lotNo}${l.expiryDate ? ` · ${new Date(l.expiryDate).toLocaleDateString('tr-TR')}` : ''}` : '—'}</TableCell>
                  {count.showsExpected && <TableCell align="right">{fmtQty(l.snapshotQty)} {l.unit}</TableCell>}
                  <TableCell align="right">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={700} color={l.countedQty == null ? 'text.secondary' : undefined}>
                        {l.countedQty == null ? tt('sayılmadı', 'not counted') : `${fmtQty(l.countedQty)} ${l.unit || ''}`}
                      </Typography>
                      {count.canEnterManually && (
                        <Tooltip title={tt('Toplamı düzelt', 'Correct total')}>
                          <IconButton size="small" onClick={() => { setEditLine(l); setEditText(l.countedQty != null ? String(l.countedQty).replace('.', ',') : ''); }}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{fmtDate(l.lastCountedAt)}</TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow><TableCell colSpan={count.showsExpected ? 6 : 5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {tt('Bu filtrede satır yok.', 'No rows in this filter.')}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, display: 'grid', gap: 1 }}>
        <Typography variant="overline" color="text.secondary">{tt('Son girişler', 'Recent entries')}</Typography>
        {entries.length === 0 && <Typography variant="body2" color="text.secondary">{tt('Henüz giriş yok.', 'No entries yet.')}</Typography>}
        {entries.map((e) => (
          <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: e.isUndone || e.method === 'UNDO' ? 0.55 : 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {e.itemName || (e.method === 'UNKNOWN' ? `${tt('Tanınmayan', 'Unknown')} ${e.barcode}` : e.barcode)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {e.method === 'UNDO' ? tt('geri alma', 'undo') : e.entryUnit ? `${fmtQty(e.entryQuantity ?? 1)} ${e.entryUnit}` : ''}
                {' '}{e.quantity > 0 ? '+' : ''}{fmtQty(e.quantity)} · {e.countedByName || ''} · {fmtDate(e.createdAt)}
              </Typography>
            </Box>
            {!e.isUndone && e.method !== 'UNDO' && (
              <Tooltip title={tt('Geri al', 'Undo')}>
                <IconButton size="small" onClick={() => undo(e)}><UndoIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
      </Paper>

      <Dialog open={!!editLine} onClose={() => setEditLine(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('Toplamı düzelt', 'Correct total')}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
          <Typography variant="body2">{editLine?.itemName}</Typography>
          <Alert severity="info" sx={{ py: 0 }}>
            {tt('Sayılan toplam bu değere getirilir; fark ayrı bir giriş olarak kaydedilir.', 'The counted total is set to this value; the difference is recorded as an entry.')}
          </Alert>
          <TextField autoFocus size="small" label={`${tt('Yeni toplam', 'New total')} (${editLine?.unit || ''})`} value={editText}
            onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveTotal(); }}
            inputProps={{ inputMode: 'decimal' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLine(null)}>{tt('Vazgeç', 'Cancel')}</Button>
          <Button variant="contained" onClick={saveTotal}>{tt('Kaydet', 'Save')}</Button>
        </DialogActions>
      </Dialog>

      <ImportCountExcelDialog
        open={importOpen}
        count={count}
        lines={lines}
        onClose={() => setImportOpen(false)}
        onDone={() => { onChanged(); loadEntries(); }}
      />

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast?.severity || 'info'} variant="filled" onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CountingPanel;
